import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, STATUS_LABELS } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";
import { Seo } from "@/components/Seo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Search, CheckCircle2, AlertTriangle } from "lucide-react";

const CATEGORIES = [
  { v: "driver_terlambat", l: "Driver Terlambat" },
  { v: "driver_tidak_datang", l: "Driver Tidak Datang" },
  { v: "armada_tidak_sesuai", l: "Armada Tidak Sesuai" },
  { v: "harga_tidak_sesuai", l: "Harga Tidak Sesuai" },
  { v: "perilaku", l: "Perilaku Tidak Sopan" },
  { v: "barang_tertinggal", l: "Barang Tertinggal" },
  { v: "pembatalan", l: "Pembatalan" },
  { v: "refund", l: "Refund" },
  { v: "keselamatan", l: "Keselamatan" },
  { v: "pembayaran", l: "Masalah Pembayaran" },
];

const COMPLAINT_LABELS = {
  baru: "Baru", diproses: "Diproses", menunggu_bukti: "Menunggu Bukti",
  menunggu_driver: "Menunggu Tanggapan Driver", diselesaikan: "Diselesaikan",
  ditolak: "Ditolak", diteruskan: "Diteruskan",
};

export default function ComplaintPage() {
  const { orderNumber } = useParams();
  const [input, setInput] = useState(orderNumber || "");
  const [order, setOrder] = useState(null);
  const [existing, setExisting] = useState([]);
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ reporter_name: "", category: "driver_terlambat", description: "" });

  const fetchOrder = async (num) => {
    if (!num) return;
    setLoading(true);
    setChecked(false);
    setSubmitted(false);
    try {
      const clean = num.trim().toUpperCase();
      const { data } = await api.get(`/orders/${clean}`);
      setOrder(data);
      const comps = await api.get(`/complaints/order/${clean}`);
      setExisting(comps.data);
    } catch (e) {
      setOrder(null);
      toast.error("Nomor order tidak ditemukan");
    } finally {
      setChecked(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderNumber) fetchOrder(orderNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderNumber]);

  const submit = async () => {
    if (!form.description.trim()) { toast.error("Ceritakan kronologi pengaduan Anda"); return; }
    setLoading(true);
    try {
      await api.post("/complaints", { order_number: order.order_number, ...form });
      setSubmitted(true);
      toast.success("Pengaduan terkirim. Admin akan menindaklanjuti.");
      const comps = await api.get(`/complaints/order/${order.order_number}`);
      setExisting(comps.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo title="Ajukan Pengaduan | Travele" description="Ajukan pengaduan atau sengketa terkait order travel Anda di Travele." noindex />
      <Navbar />
      <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6">
        <p className="font-mono-terminal text-xs uppercase tracking-widest text-primary">Pengaduan & Sengketa</p>
        <h1 className="mt-2 font-display text-3xl font-black uppercase tracking-tighter sm:text-4xl">Ajukan Pengaduan</h1>
        <p className="mt-2 text-muted-foreground">Masukkan nomor order Anda untuk menyampaikan pengaduan terkait perjalanan.</p>

        <div className="mt-6 flex gap-2">
          <Input data-testid="complaint-order-input" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Contoh: TRV-000001" onKeyDown={(e) => e.key === "Enter" && fetchOrder(input)} />
          <Button data-testid="complaint-order-search" onClick={() => fetchOrder(input)} disabled={loading} className="bg-primary font-bold text-primary-foreground hover:bg-primary/90">
            <Search className="mr-1 h-4 w-4" /> Cek
          </Button>
        </div>

        {checked && order && (
          <div className="mt-8 space-y-6">
            {existing.length > 0 && (
              <div className="border border-border bg-card p-5" data-testid="complaint-existing">
                <h3 className="mb-3 font-display text-lg font-bold">Riwayat Pengaduan</h3>
                {existing.map((c) => (
                  <div key={c.id} className="mb-2 flex items-center justify-between border-b border-border pb-2 text-sm last:border-0">
                    <div>
                      <div className="font-medium">{CATEGORIES.find((x) => x.v === c.category)?.l || c.category}</div>
                      <div className="text-xs text-muted-foreground">{c.description.slice(0, 60)}</div>
                    </div>
                    <Badge variant="secondary">{COMPLAINT_LABELS[c.status] || c.status}</Badge>
                  </div>
                ))}
              </div>
            )}

            {submitted ? (
              <div className="border border-border bg-card p-6 text-center" data-testid="complaint-thankyou">
                <CheckCircle2 className="mx-auto mb-3 h-14 w-14 text-emerald-400" />
                <h3 className="font-display text-xl font-bold">Pengaduan Terkirim</h3>
                <p className="mt-1 text-sm text-muted-foreground">Admin Travele akan meninjau pengaduan Anda. Anda dapat memantau statusnya di halaman ini.</p>
                <Link to="/"><Button variant="secondary" className="mt-4">Kembali ke Beranda</Button></Link>
              </div>
            ) : (
              <div className="border border-border bg-card p-6" data-testid="complaint-form">
                <div className="mb-4 flex items-start gap-2 border border-amber-500/40 bg-amber-500/5 p-3 text-xs text-amber-300">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  Order {order.order_number} · {order.origin} → {order.destination} · Status: {STATUS_LABELS[order.status] || order.status}
                </div>
                <div className="grid gap-4">
                  <div className="grid gap-1.5">
                    <Label>Nama Pelapor</Label>
                    <Input data-testid="complaint-name" value={form.reporter_name} onChange={(e) => setForm({ ...form, reporter_name: e.target.value })} placeholder={order.passenger_name} />
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Kategori Pengaduan</Label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger data-testid="complaint-category"><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-1.5">
                    <Label>Kronologi</Label>
                    <Textarea data-testid="complaint-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Jelaskan apa yang terjadi..." rows={5} />
                  </div>
                  <Button data-testid="complaint-submit" onClick={submit} disabled={loading} className="w-full bg-primary font-bold text-primary-foreground hover:bg-primary/90">
                    {loading ? "Mengirim..." : "Kirim Pengaduan"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {checked && !order && (
          <p className="mt-6 border border-border bg-card p-6 text-center text-muted-foreground" data-testid="complaint-notfound">
            Nomor order tidak ditemukan. Periksa kembali nomor order Anda.
          </p>
        )}
      </div>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
